function hexToRgb(hex) {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2];
  if (h.length === 4) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2]+h[3]+h[3];
  const int = parseInt(h.slice(0, 6), 16);
  return {
    r: (int >> 16) & 255,
    g: (int >>  8) & 255,
    b:  int        & 255,
    a: h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1,
  };
}

function rgbToHex({ r, g, b }) {
  return '#' + [r, g, b].map(x => Math.round(Math.max(0, Math.min(255, x))).toString(16).padStart(2, '0')).join('');
}

function hexToHsl(hex) {
  const { r, g, b } = hexToRgb(hex);
  const rn = r/255, gn = g/255, bn = b/255;
  const max = Math.max(rn,gn,bn), min = Math.min(rn,gn,bn);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn: h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6; break;
      case gn: h = ((bn - rn) / d + 2) / 6; break;
      default: h = ((rn - gn) / d + 4) / 6;
    }
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToHex({ h, s, l }) {
  const sn = s / 100, ln = l / 100;
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1/6) return p + (q-p)*6*t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q-p)*(2/3-t)*6;
    return p;
  };
  let r, g, b;
  if (sn === 0) {
    r = g = b = ln;
  } else {
    const q = ln < 0.5 ? ln*(1+sn) : ln+sn-ln*sn;
    const p = 2*ln - q;
    const hn = ((h % 360) + 360) % 360 / 360;
    r = hue2rgb(p, q, hn + 1/3);
    g = hue2rgb(p, q, hn);
    b = hue2rgb(p, q, hn - 1/3);
  }
  return rgbToHex({ r: r*255, g: g*255, b: b*255 });
}

function getLuminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  const lin = c => { const s = c/255; return s <= 0.03928 ? s/12.92 : Math.pow((s+0.055)/1.055, 2.4); };
  return 0.2126*lin(r) + 0.7152*lin(g) + 0.0722*lin(b);
}

function getContrast(hex1, hex2) {
  const l1 = getLuminance(hex1), l2 = getLuminance(hex2);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

export function getContrastText(bgHex) {
  return getContrast(bgHex, '#ffffff') >= 4.5 ? '#ffffff' : '#000000';
}

function adjustLightness(hex, delta) {
  const hsl = hexToHsl(hex);
  return hslToHex({ ...hsl, l: Math.min(100, Math.max(0, hsl.l + delta)) });
}

function mixColors(hex1, hex2, weight = 0.5) {
  const c1 = hexToRgb(hex1), c2 = hexToRgb(hex2);
  return rgbToHex({
    r: c1.r + (c2.r - c1.r) * weight,
    g: c1.g + (c2.g - c1.g) * weight,
    b: c1.b + (c2.b - c1.b) * weight,
  });
}

function transparentize(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

function generateColorScale(primaryHex) {
  const stops = { 50:92, 100:85, 200:72, 300:55, 400:38, 500:0, 600:-12, 700:-22, 800:-32, 900:-42 };
  const out = {};
  for (const [key, delta] of Object.entries(stops)) {
    if (delta === 0)       out[key] = primaryHex;
    else if (delta > 0)    out[key] = mixColors(primaryHex, '#ffffff', delta/100);
    else                   out[key] = adjustLightness(primaryHex, delta);
  }
  return out;
}

function deriveGradientEnd(primaryHex) {
  const { h, s, l } = hexToHsl(primaryHex);
  return hslToHex({ h: (h + 25) % 360, s: Math.min(s + 5, 100), l: Math.max(l - 8, 20) });
}

export class ColorEngine {
  constructor(rawCfg) {
    this._raw = rawCfg;
    this._resolved = {};
  }

  resolve() {
    const raw    = this._raw;
    const p      = raw.primaryColor || '#5046e4';
    const isDark = raw.theme === 'dark';
    const pe     = raw.primaryGradientEnd || deriveGradientEnd(p);
    const angle  = raw.primaryGradientAngle || 135;
    const scale  = generateColorScale(p);

    this._resolved = {
      '--mc-grad-start':        p,
      '--mc-grad-end':          pe,
      '--mc-grad-angle':        `${angle}deg`,
      '--mc-gradient':          `linear-gradient(${angle}deg, ${p}, ${pe})`,
      '--mc-p50':  scale[50],  '--mc-p100': scale[100], '--mc-p200': scale[200],
      '--mc-p300': scale[300], '--mc-p400': scale[400], '--mc-p500': scale[500],
      '--mc-p600': scale[600], '--mc-p700': scale[700], '--mc-p800': scale[800], '--mc-p900': scale[900],
      '--mc-bg':                raw.bgColor || (isDark ? '#0f0f1a' : '#ffffff'),
      '--mc-bg-messages':       raw.bgMessagesColor || (isDark ? '#13131f' : adjustLightness(p, 47)),
      '--mc-surface':           isDark ? '#1a1a2e' : '#ffffff',
      '--mc-surface-2':         isDark ? '#16162a' : adjustLightness(p, 46),
      '--mc-header-text':       raw.headerTextColor || '#ffffff',
      '--mc-bot-bubble-bg':     raw.botBubbleColor || (isDark ? adjustLightness(p, 10) : '#ffffff'),
      '--mc-bot-bubble-text':   raw.botBubbleTextColor || (isDark ? '#e8e8f8' : '#1a1a2e'),
      '--mc-bot-bubble-border': isDark ? transparentize(p, 0.2) : 'rgba(0,0,0,0.06)',
      '--mc-user-bubble-bg':    raw.userBubbleColor || `linear-gradient(${angle}deg, ${p}, ${pe})`,
      '--mc-user-bubble-text':  raw.userBubbleTextColor || getContrastText(p),
      '--mc-chip-border':       raw.chipBorderColor || transparentize(p, 0.25),
      '--mc-chip-text':         raw.chipTextColor || p,
      '--mc-chip-bg':           raw.chipBgColor || transparentize(p, 0.06),
      '--mc-chip-hover-bg':     p,
      '--mc-chip-hover-text':   getContrastText(p),
      '--mc-input-bg':          raw.inputBgColor || (isDark ? adjustLightness(p, 8) : adjustLightness(p, 46)),
      '--mc-input-text':        raw.inputTextColor || (isDark ? '#e8e8f8' : '#1a1a2e'),
      '--mc-input-placeholder': raw.inputPlaceholderColor || transparentize(p, isDark ? 0.55 : 0.45),
      '--mc-input-border-focus': p,
      '--mc-send-bg':           raw.sendButtonColor || `linear-gradient(${angle}deg, ${p}, ${pe})`,
      '--mc-send-icon':         raw.sendButtonIconColor || getContrastText(p),
      '--mc-typing-dot':        raw.typingDotColor || (isDark ? adjustLightness(p, 30) : adjustLightness(p, 25)),
      '--mc-timestamp':         raw.timestampColor || (isDark ? adjustLightness(p, 25) : adjustLightness(p, 28)),
      '--mc-status-dot':        raw.statusDotColor || '#34d399',
      '--mc-launcher-bg':       raw.launcherColor || `linear-gradient(${angle}deg, ${p}, ${pe})`,
      '--mc-launcher-icon':     raw.launcherIconColor || '#ffffff',
      '--mc-badge-bg':          raw.badgeColor || '#f43f5e',
      '--mc-shadow-color':      raw.shadowColor || transparentize(p, 0.25),
      '--mc-shadow-panel':      `0 24px 56px ${raw.shadowColor || transparentize(p, 0.22)}, 0 8px 16px ${transparentize(p, 0.12)}`,
      '--mc-footer-text':       raw.footerTextColor || (isDark ? adjustLightness(p, 25) : adjustLightness(p, 28)),
      '--mc-footer-link':       raw.footerLinkColor || p,
      '--mc-border':            isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
      '--mc-border-strong':     isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)',
      '--mc-scrollbar-thumb':   adjustLightness(p, 35),
      '--mc-scrollbar-track':   'transparent',
      '--mc-g-desktop-width':   raw.floatpanelDesktopWidth || '50vw',
      '--mc-g-mobile-width':    raw.floatpanelMobileWidth  || '92vw',
      '--mc-g-input-border':    raw.floatpanelInputBorderColor || (isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.12)'),
      '--mc-g-gc1':             raw.floatpanelGradientColor1 || '#4285F4',
      '--mc-g-gc2':             raw.floatpanelGradientColor2 || '#8B5CF6',
      '--mc-g-gc3':             raw.floatpanelGradientColor3 || '#EC4899',
      '--mc-g-gc4':             raw.floatpanelGradientColor4 || '#06B6D4',
      '--mc-g-panel-bg':        raw.floatpanelPanelBg || (isDark ? '#0f0f1a' : '#ffffff'),
      '--mc-g-backdrop': (() => {
        const bg      = raw.floatpanelPanelBg || (isDark ? '#0f0f1a' : '#ffffff');
        const fadePct = Math.max(10, Math.min(80, raw.floatpanelBackdropFrom || 50));
        const { r, g, b } = hexToRgb(bg);
        const steps = 20;
        const stops = [`rgba(${r},${g},${b},0) 0%`];
        for (let i = 1; i <= steps; i++) {
          const t = i / steps;
          const s = t * t * (3 - 2 * t);
          const alpha = +s.toFixed(3);
          const pct   = Math.round(t * fadePct);
          stops.push(`rgba(${r},${g},${b},${alpha}) ${pct}%`);
        }
        stops.push(`${bg} 100%`);
        return `linear-gradient(to bottom, ${stops.join(', ')})`;
      })(),
      '--mc-g-icon':          raw.floatpanelIconColor || (isDark ? adjustLightness(p, 30) : p),
      '--mc-r-panel':         `${raw.panelRadius    || 20}px`,
      '--mc-r-bubble':        '16px',
      '--mc-r-chip':          '999px',
      '--mc-r-input':         '12px',
      '--mc-r-send':          '10px',
      '--mc-r-launcher':      `${raw.launcherRadius != null ? raw.launcherRadius : 50}%`,
      '--mc-width':           `${raw.width        || 370}px`,
      '--mc-height':          `${raw.height       || 580}px`,
      '--mc-launcher-size':   `${raw.launcherSize  || 52}px`,
      '--mc-z':               `${raw.zIndex       || 9999}`,
      '--mc-offset-x':        `${raw.offsetX      || 24}px`,
      '--mc-offset-y':        `${raw.offsetY      || 24}px`,
      '--mc-ginput-opacity':  `${raw.floatpanelInputOpacity != null ? Math.min(1, Math.max(0, raw.floatpanelInputOpacity)) : 0.72}`,
    };
    return this._resolved;
  }

  applyTo(el) {
    const vars = this.resolve();
    Object.entries(vars).forEach(([k, v]) => el.style.setProperty(k, v));
  }

  setColor(property, value, el) {
    const map = {
      primary:    ['--mc-grad-start','--mc-p500','--mc-chip-text','--mc-footer-link','--mc-input-border-focus'],
      primaryEnd: ['--mc-grad-end'],
      botBubble:  ['--mc-bot-bubble-bg'],
      userBubble: ['--mc-user-bubble-bg'],
      background: ['--mc-bg'],
    };
    const targets = map[property] || [property];
    targets.forEach(t => el.style.setProperty(t, value));
  }
}
